import { useMemo, useState } from "react";
import { Plus, Trash2, Loader2, WifiOff, RefreshCw, Pencil } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import DataTable from "@/components/dashboard/DataTable";
import LoadError from "@/components/dashboard/LoadError";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useApiData } from "@/hooks/useApiData";
import { usePlans } from "@/hooks/usePlans";
import { isCouponEligiblePlan } from "@/data/plans";
import { listCoupons, createCoupon, updateCoupon, deleteCoupon } from "@/api/admin";
import { fieldErrors } from "@/api/client";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/dates";
import { formatMoney } from "@/lib/money";

const DEMO = { coupons: [{ id: "c1", code: "LAUNCH20", description: "Launch offer", discount_type: "percent", discount_value: 20, redeemed_count: 12, max_redemptions: 100, active: true, applies_to_plans: [] }] };
const BLANK = { code: "", description: "", discount_type: "percent", discount_value: 10, currency: "INR", applies_to_plans: [], max_redemptions: null, expires_at: null, active: true };

// Mirrors backend/api/routers/admin_coupons.py CODE_MAX_LENGTH, which is itself
// the `coupons.code` column width (String(40)). The client copy exists so the
// field can stop at the limit instead of collecting characters the server will
// refuse; the server is still the control.
const CODE_MAX_LENGTH = 40;
const CODE_MIN_LENGTH = 2;

// Rows per page. Named rather than inlined on the table so the number a reader
// sees in the footer has somewhere to be changed.
const PAGE_SIZE = 10;

// The state chips. KEYS are unchanged from when these read "Live" and "Off" —
// only the labels moved — so every chip still selects exactly the same rows.
const STATE_FILTERS = [
  { key: "live", label: "Active" },
  { key: "expired", label: "Expired" },
  { key: "exhausted", label: "Used up" },
  { key: "off", label: "Inactive" },
];

// One badge per state. `live` is the only one that means "this code works right
// now", so it is the only green: an expired or fully-redeemed coupon still has
// `active === true` and would otherwise be shown as Active while rejecting every
// redemption at checkout.
const STATE_BADGE = {
  live: { label: "Active", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" },
  expired: { label: "Expired", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" },
  exhausted: { label: "Used up", cls: "bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300" },
  off: { label: "Inactive", cls: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300" },
};

// Why a coupon isn't working, in one word. `active` alone can't say: an expired
// or fully-redeemed code is flagged active and rejects every redemption.
//
// The order and the comparisons match validate_coupon's own checks, so this can
// never disagree with what happens at checkout. Expiry is judged on the INSTANT
// (`expires_at`), not on the displayed date, because a coupon stays valid
// through the whole of its last day.
function couponState(c) {
  if (!c.active) return "off";
  if (c.expires_at && new Date(c.expires_at).getTime() <= Date.now()) return "expired";
  if (c.max_redemptions != null && (c.redeemed_count || 0) >= c.max_redemptions) return "exhausted";
  return "live";
}

// The expiry as the DAY the coupon stops working.
//
// The API sends `expires_on` — the UTC calendar date — precisely so no client has
// to reason about this. `expires_at` is the last INSTANT of that day in UTC, and
// rendering that instant in the viewer's own timezone showed the next day for
// every reader east of UTC (23:59:59Z is 05:29 on the 11th in IST), which is why
// the list and the edit form disagreed about the same coupon. The fallback
// derives the same UTC date locally, so a cached payload from before the field
// existed still renders the right day.
function expiryDate(c) {
  if (c.expires_on) return c.expires_on;
  if (!c.expires_at) return null;
  const d = new Date(c.expires_at);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

function fmtDiscount(c) {
  return c.discount_type === "percent"
    ? `${c.discount_value}% off`
    : `${formatMoney(Number(c.discount_value) || 0, c.currency || "INR")} off`;
}

function ActionBtn({ title, label, onClick, tone, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        "grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors",
        tone === "rose"
          ? "hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-500/10"
          : "hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
      )}
    >
      {children}
    </button>
  );
}

export default function Coupons() {
  const { data, loading, demo, error, reload } = useApiData(listCoupons, DEMO);
  // Memoised: `(data && data.coupons) || []` is a new array every render, which
  // makes every memo downstream of it recompute on each keystroke.
  const rows = useMemo(() => (data && data.coupons) || [], [data]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  // Shown inside the modal footer, not in page flow — the modal is a scroll-locked
  // overlay, so a banner behind its scrim is unreachable.
  const [saveError, setSaveError] = useState("");
  // Per-field messages the SERVER rejected the save with, keyed by field name.
  // Without this a validation failure could only ever be a footer banner, so the
  // operator was told something was wrong but not which box — and the offending
  // field was not highlighted at all.
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  // The coupon awaiting delete confirmation — a coupon is redeemable at
  // checkout, so removing the wrong one silently breaks a live promotion.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // A coupon's REAL state, not just its `active` flag: an expired or fully
  // redeemed coupon reads as live in a list that only shows on/off, and the
  // operator has no way to tell why it keeps failing at checkout.
  const withState = useMemo(
    () => rows.map((c) => ({ ...c, state: couponState(c) })), [rows]);

  // The live plan catalog, so the "applies to" picker offers real ids rather
  // than asking the operator to remember them.
  const { plans: planList } = usePlans();
  const planName = useMemo(() => {
    const map = {};
    planList.forEach((p) => { map[p.id] = p.name || p.id; });
    return map;
  }, [planList]);

  // Which plans may CARRY a coupon — the same rule the API enforces, read off
  // each plan's price rather than off its id (see data/plans.js). This is what
  // keeps the Free Trial (and any other free or contact-sales plan, including
  // ones created later) out of the picker.
  //
  // Ids already on the coupon being edited stay listed even if they are no longer
  // eligible — a plan may have been made free after the coupon was written, and
  // silently dropping a target the operator can still see stored would be worse
  // than showing it so they can remove it deliberately.
  // Memoised so the `[]` branch isn't a fresh array on every render, which would
  // make the picker memo below recompute on each keystroke anywhere in the form.
  const selectedPlans = useMemo(
    () => (Array.isArray(form.applies_to_plans) ? form.applies_to_plans : []),
    [form.applies_to_plans]);
  const pickerPlans = useMemo(() => {
    const already = new Set(selectedPlans);
    return planList.filter((p) => isCouponEligiblePlan(p) || already.has(p.id));
  }, [planList, selectedPlans]);

  const today = new Date().toISOString().slice(0, 10);

  // Inline field errors mirroring the API's own rules, so Save isn't the first
  // thing that tells the operator a value is out of range.
  const trimmedCode = form.code.trim();
  const codeError =
    serverFieldErrors.code
    || (!trimmedCode
      ? ""
      : trimmedCode.length < CODE_MIN_LENGTH
        ? `A coupon code needs at least ${CODE_MIN_LENGTH} characters.`
        : trimmedCode.length > CODE_MAX_LENGTH
          ? `A coupon code can be at most ${CODE_MAX_LENGTH} characters — this one is ${trimmedCode.length}.`
          : "");
  const discountValue = Number(form.discount_value);
  const discountError = serverFieldErrors.discount_value || (!form.discount_value
    ? ""
    : !Number.isFinite(discountValue) || discountValue <= 0
      ? "Enter a number above 0."
      : form.discount_type === "percent" && discountValue >= 100
        ? "A percentage discount has to be less than 100."
        : "");
  const redemptionsError = serverFieldErrors.max_redemptions || (!form.max_redemptions
    ? ""
    : !Number.isInteger(Number(form.max_redemptions)) || Number(form.max_redemptions) < 1
      ? "Leave blank for unlimited, or enter 1 or more."
      : "");
  const expiryError = serverFieldErrors.expires_at
    || (form.expires_at && String(form.expires_at).slice(0, 10) < today
      ? "That date has passed — the coupon would reject every redemption."
      : "");
  const formInvalid = !trimmedCode || !!codeError || !!discountError
    || !!redemptionsError || !!expiryError || !form.discount_value;

  function open(c) {
    setSaveError("");
    setServerFieldErrors({});
    setEditing(c || {});
    // Kept as an ARRAY throughout now that the field is a picker — the old
    // join/split round trip through a comma string was where stray whitespace
    // and typos got in.
    setForm(c ? { ...BLANK, ...c, applies_to_plans: c.applies_to_plans || [] } : { ...BLANK });
  }
  function closeModal() {
    setEditing(null);
    setSaveError("");
    setServerFieldErrors({});
  }
  async function save() {
    setBusy(true);
    try {
      const payload = { ...form, code: trimmedCode, discount_value: Number(form.discount_value),
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        // The picker gives a bare date; the coupon stays valid through the whole
        // of that day, so store it as end-of-day UTC. `expires_on` in the
        // response is derived from this same instant, in UTC, so what the list
        // shows is the day that was picked here.
        expires_at: form.expires_at ? new Date(`${String(form.expires_at).slice(0, 10)}T23:59:59Z`).toISOString() : null,
        applies_to_plans: Array.isArray(form.applies_to_plans) ? form.applies_to_plans : [] };
      if (editing.id) await updateCoupon(editing.id, payload); else await createCoupon(payload);
      closeModal(); await reload();
    } catch (e) {
      // A 422 carries per-field messages; put them ON the fields and keep the
      // banner for everything else (a 409 duplicate code, a 400 rule, a network
      // failure). fieldErrors() is empty for those, so nothing is lost.
      const fields = fieldErrors(e);
      setServerFieldErrors(fields);
      setSaveError(Object.keys(fields).length ? "" : (e.message || "Failed to save."));
    }
    finally { setBusy(false); }
  }
  async function remove() {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    try {
      await deleteCoupon(pendingDelete.id);
      setPendingDelete(null);
      await reload();
    } catch (e) {
      toastError(e.message || "Couldn't delete the coupon.");
    } finally {
      setDeleting(false);
    }
  }

  // One column set for the whole list. This was a grid of cards, which showed
  // the code and one cramped meta line and nothing else — the plans a coupon
  // applied to were invisible without opening it, and there was no way to sort
  // by usage or expiry.
  const columns = [
    {
      key: "code", header: "Code", sortable: true, render: (c) => (
        <div className="min-w-0">
          {/* break-all, not truncate: a code is the thing being looked up, so a
              long one must stay readable rather than end in an ellipsis. */}
          <p className="break-all font-mono font-semibold text-slate-800 dark:text-white">{c.code}</p>
          {c.description && (
            <p className="mt-0.5 line-clamp-1 text-xs text-slate-400" title={c.description}>{c.description}</p>
          )}
        </div>
      ),
    },
    {
      key: "discount", header: "Discount", sortable: true,
      render: (c) => <span className="whitespace-nowrap text-sm text-slate-700 dark:text-slate-200">{fmtDiscount(c)}</span>,
    },
    {
      key: "plans", header: "Applies to", render: (c) => {
        const ids = c.applies_to_plans || [];
        // An empty list is "no restriction" at checkout — say that, rather than
        // rendering nothing and leaving it looking unconfigured.
        if (!ids.length) return <span className="text-sm text-slate-500 dark:text-slate-400">All plans</span>;
        const shown = ids.slice(0, 2);
        const names = ids.map((id) => planName[id] || id);
        return (
          <div className="flex flex-wrap items-center gap-1" title={names.join(", ")}>
            {shown.map((id) => (
              <Badge key={id} tone="brand" className="max-w-[10rem] truncate">{planName[id] || id}</Badge>
            ))}
            {ids.length > shown.length && (
              <span className="text-xs text-slate-400">+{ids.length - shown.length}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "usage", header: "Used", align: "right", sortable: true,
      render: (c) => (
        <span className="whitespace-nowrap tabular-nums text-sm text-slate-700 dark:text-slate-200">
          {c.redeemed_count || 0} / {c.max_redemptions ?? "∞"}
        </span>
      ),
    },
    {
      key: "expiry", header: "Expires", sortable: true,
      render: (c) => {
        // formatDate reads a bare "YYYY-MM-DD" straight off the string with no
        // timezone conversion at all — see lib/dates.js — so the day rendered
        // here is the day that was stored, for every reader.
        const on = expiryDate(c);
        return (
          <span className={cn("whitespace-nowrap text-sm",
            on ? "text-slate-700 dark:text-slate-200" : "text-slate-400")}>
            {on ? formatDate(on) : "Unlimited"}
          </span>
        );
      },
    },
    {
      key: "state", header: "Status", render: (c) => {
        const b = STATE_BADGE[c.state] || STATE_BADGE.off;
        return (
          <Badge className={cn("whitespace-nowrap", b.cls)}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" /> {b.label}
          </Badge>
        );
      },
    },
    {
      key: "actions", header: "Actions", align: "right", render: (c) => (
        <div className="flex justify-end gap-1">
          <ActionBtn title="Edit coupon" label={`Edit coupon ${c.code}`} onClick={() => open(c)}>
            <Pencil size={15} />
          </ActionBtn>
          <ActionBtn title="Delete coupon" label={`Delete coupon ${c.code}`} tone="rose" onClick={() => setPendingDelete(c)}>
            <Trash2 size={15} />
          </ActionBtn>
        </div>
      ),
    },
  ];

  // Counts live on the chips so the operator can see the shape of the catalog
  // without clicking through each filter.
  const stateOptions = STATE_FILTERS.map((f) => ({
    value: f.key,
    label: f.label,
    count: withState.filter((c) => c.state === f.key).length,
  }));

  // Sortable columns read the row, not the renderer, so the derived values the
  // table sorts and searches on have to exist as fields.
  const tableRows = useMemo(() => withState.map((c) => ({
    ...c,
    discount: c.discount_type === "percent" ? Number(c.discount_value) || 0 : Number(c.discount_value) || 0,
    usage: c.redeemed_count || 0,
    // A coupon that never expires sorts last rather than first — "no expiry" is
    // the furthest-away date, not the earliest.
    expiry: expiryDate(c) || "9999-12-31",
  })), [withState]);

  return (
    <>
      <PageHeader title="Coupons" subtitle="Discount codes applied at checkout."
        actions={<><Button variant="outline" size="sm" onClick={reload}><RefreshCw size={16} /> Refresh</Button><Button size="sm" onClick={() => open(null)}><Plus size={16} /> New coupon</Button></>} />
      {/* dark:text-amber-200 — without it this banner kept text-amber-800 on a
          dark translucent panel and was effectively unreadable in dark mode. */}
      {demo && <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200"><WifiOff size={15} className="shrink-0" /> Demo data — backend not reachable.</div>}

      <LoadError message={error} onRetry={reload} />

      {loading ? <div className="flex justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>
        : error ? null
        : (
          <DataTable
            columns={columns}
            rows={tableRows}
            getRowId={(c) => c.id}
            onRowClick={open}
            searchKeys={["code", "description"]}
            filters={[{ key: "state", label: "Status", total: tableRows.length, options: stateOptions }]}
            initialSort={{ key: "code", dir: "asc" }}
            maxHeight="40rem"
            pageSize={PAGE_SIZE}
            emptyLabel="No coupons yet."
          />
        )}

      <Modal open={!!editing} onClose={closeModal} title={editing?.id ? "Edit coupon" : "New coupon"} size="md"
        footer={<div className="flex flex-col gap-2">
          {saveError && <p className="text-xs font-medium text-rose-500">{saveError}</p>}
          <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={closeModal}>Cancel</Button><Button size="sm" onClick={save} disabled={busy || formInvalid}>{busy ? <Loader2 size={15} className="animate-spin" /> : "Save"}</Button></div>
        </div>}>
        <div className="space-y-4">
          <Input
            label="Code"
            required
            placeholder="LAUNCH20"
            // Stops at the column width instead of collecting characters the
            // server can only refuse. The inline rule below still fires, because
            // maxLength doesn't apply to a pasted-then-edited value in every
            // browser and says nothing about the minimum.
            maxLength={CODE_MAX_LENGTH}
            value={form.code}
            onChange={(e) => {
              const code = e.target.value.toUpperCase();
              setForm((f) => ({ ...f, code }));
              // A server message about the old value must not sit under the new
              // one the operator is typing to fix it.
              setServerFieldErrors((prev) => (prev.code ? { ...prev, code: undefined } : prev));
            }}
            error={codeError}
          />
          <Input label="Description" value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          {/* items-start so a validation message under one field can't shunt the
              other one down; one column below `sm` so neither is crushed. */}
          <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
            {/* Select, not a bare <select>: it is built to the same label
                typography, control height and radius as Input, which is what
                makes this pair line up. No asterisk — a dropdown that always
                holds one of two values can never be left empty, and marking it
                required would not match any rule that actually blocks Save. */}
            <Select label="Type" value={form.discount_type}
              onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}>
              <option value="percent">Percent</option>
              <option value="amount">Fixed amount</option>
            </Select>
            {/* Bounds mirror the API exactly (discount_value gt=0, percent < 100),
                so a bad figure is caught at the keyboard instead of coming back
                as a 422 after the round trip. */}
            <Input
              label={form.discount_type === "percent" ? "Percent (1–99)" : `Amount (${form.currency || "INR"})`}
              required
              type="number" min="1" step={form.discount_type === "percent" ? "1" : "0.01"}
              max={form.discount_type === "percent" ? "99" : undefined}
              value={form.discount_value}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, discount_value: v }));
                setServerFieldErrors((prev) => (prev.discount_value ? { ...prev, discount_value: undefined } : prev));
              }}
              error={discountError}
            />
          </div>
          <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
            <Input label="Max redemptions (blank = ∞)" type="number" min="1" step="1"
              value={form.max_redemptions ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
              error={redemptionsError} />
            {/* `min` today: a past expiry stores cleanly and then rejects every
                redemption — a coupon that looks live and works for nobody. The
                API refuses it too. */}
            <Input label="Expires (blank = never)" type="date" min={today}
              value={form.expires_at ? String(form.expires_at).slice(0, 10) : ""}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value || null }))}
              error={expiryError} />
          </div>
          {/* Chosen from the LIVE catalog, filtered to the plans that can carry a
              discount at all. This was free text validated only by the server, so
              the operator had to already know the plan ids and a typo produced a
              coupon that applied to nothing. */}
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Applies to
            </span>
            <p className="mb-2 text-xs text-slate-400">
              Select nothing to apply this to every plan. Free and contact-sales
              plans aren&apos;t listed — there is no charge on them to discount.
            </p>
            {planList.length === 0 ? (
              <p className="text-xs text-slate-400">Loading plans…</p>
            ) : pickerPlans.length === 0 ? (
              <p className="text-xs text-slate-400">No paid plans to target yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {pickerPlans.map((pl) => {
                  const on = selectedPlans.includes(pl.id);
                  return (
                    <button
                      key={pl.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setForm((f) => ({
                        ...f,
                        applies_to_plans: on
                          ? selectedPlans.filter((x) => x !== pl.id)
                          : [...selectedPlans, pl.id],
                      }))}
                      className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors",
                        on ? "bg-brand-600 text-white"
                           : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10")}
                    >
                      {pl.name || pl.id}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="accent-brand-600" /> Active</label>
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingDelete}
        title={pendingDelete ? `Delete coupon ${pendingDelete.code}?` : ""}
        body="Anyone still holding this code will be rejected at checkout. This cannot be undone."
        busy={deleting}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
